import { useMutation, useQueryClient } from '@tanstack/react-query'

import api from '@/lib/api'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'

type PrimitiveField = string | number | boolean

export interface ReconsultarPendentesEsocialResponse {
  statusCode: number
  message: string
  data: {
    totalProtocolos: number
    totalConsultasComSucesso: number
    totalProcessados: number
    protocolos: string[]
    resultados: Array<Record<string, unknown>>
  }
}

export interface ReconsultarPendentesEsocialRequest {
  certFile?: File
  certPassword?: string
}

function appendField(formData: FormData, key: string, value?: PrimitiveField) {
  if (value === undefined || value === null) return
  formData.append(key, String(value))
}

export function useReconsultarPendentesEsocial(idPrevidenciario?: string) {
  const queryClient = useQueryClient()
  const token = useAccessToken()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async (
      request: ReconsultarPendentesEsocialRequest
    ): Promise<ReconsultarPendentesEsocialResponse> => {
      if (!idPrevidenciario) {
        throw new Error('ID previdenciário não informado')
      }

      if (!request.certFile) {
        throw new Error('Certificado A1 (.pfx) é obrigatório para reconsultar lotes.')
      }

      if (!request.certPassword?.trim()) {
        throw new Error('Senha do certificado é obrigatória para reconsultar lotes.')
      }

      if (!user || !token) {
        throw new Error('Usuário não autenticado')
      }

      const formData = new FormData()
      formData.append('cert_file', request.certFile)
      appendField(formData, 'cert_password', request.certPassword)
      appendField(formData, 'id_previdenciario', idPrevidenciario)

      const response = await api.post<ReconsultarPendentesEsocialResponse>(
        `/e-social/retornos/${idPrevidenciario}/reconsultar-pendentes`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      if (!response.data) {
        throw new Error('Erro ao reconsultar lotes pendentes do eSocial')
      }

      return response.data
    },
    onSuccess: () => {
      if (!idPrevidenciario) {
        return
      }

      queryClient.invalidateQueries({ queryKey: ['esocial-retornos', idPrevidenciario] })
      queryClient.invalidateQueries({ queryKey: ['previdenciario-detalhes', idPrevidenciario] })
    }
  })
}
